<template>
  <div class="project-detail-page" style="margin-top: 8px">
    <EntityOverviewLayout
      v-if="project"
      :leftPanelTabs="overviewLeftPanels"
      defaultLeftPanel="children"
      :panelTabs="overviewPanels"
      defaultPanel="files"
    >
      <template #top-left>
        <ProjectDetails :project="project" @updateProject="updateProjectDetails" />
      </template>

      <template #top-right>
        <DisplayImageGallery entityType="project" :entityId="project.id" />
      </template>

      <template #left-panel-children>
        <ProjectChildElementsList
          :elements="childElements"
          :projectId="project.id"
          :structureSetId="project.structure_set_id"
          :projectName="project.name"
          :sectionTitle="childElementsSectionTitle"
          :isOrderedList="Boolean(rootAllowedChildTypes?.[0]?.is_ordered)"
          @refresh-elements="fetchChildElements"
        />
      </template>

      <template #left-panel-experiments>
        <ExperimentList
          :experiments="experiments"
          :projectId="project.id"
          :projectName="project.name"
          :locationId="project.location_id"
        />
      </template>

      <template #panel-files>
        <ExperimentFiles
          ref="filesComp"
          :files="files"
          :folders="folders"
          :base-path="projectFilesBasePath"
          :loadingTree="isFilesTreeLoading"
          :experimentId="project.id"
          :assId="project.id"
          :assType="'project'"
          :isUploading="isUploading"
          :uploadPercent="uploadPercent"
          :uploadLabel="uploadLabel"
          @addFiles="addFiles"
          @removeFile="removeFile"
        />
      </template>

      <template #panel-comments>
        <ExperimentComments
          :comments="comments"
          :users="users"
          :files="files"
          :entityId="project.id"
          @addComment="addComment"
          @removeComment="removeComment"
          @updateComment="handleUpdateComment"
        />
      </template>

      <template #panel-materials>
        <ProjectMaterials
          :materials="materials"
          :id="project.id"
          :assType="'project'"
          :refreshMaterials="fetchProjectMaterials"
        />
      </template>
    </EntityOverviewLayout>

    <FileUploadSession
      :visible="uploadSession.visible"
      :fileType="uploadSession.fileType"
      :entityId="uploadSession.entityId"
      :files="uploadSession.files"
      :entraId="loggedInUser?.entra_id"
      :folderId="uploadSession.folderId"
      @complete="handleUploadComplete"
      @close="handleUploadClose"
      @error="handleUploadError"
    />
  </div>
</template>

<script>
import ProjectDetails from '@/components/ProjectDetails.vue';
import ProjectChildElementsList from '@/components/ProjectChildElementsList.vue';
import ExperimentList from '@/components/ExperimentList.vue';
import ExperimentFiles from '@/components/ExperimentFiles.vue';
import ExperimentComments from '@/components/ExperimentComments.vue';
import ProjectMaterials from "@/components/ProjectMaterials.vue";
import FileUploadSession from '@/components/FileUploadSession.vue';
import EntityOverviewLayout from '@/components/EntityOverviewLayout.vue';
import DisplayImageGallery from '@/components/DisplayImageGallery.vue';
import { useEntityDiscussion } from '@/composables/useEntityDiscussion';
import { parseFilesTree } from '@/utils/filesTree';
import axios from 'axios';
import { API_ENDPOINTS } from '@/services/config';
import apiService from '@/services/apiService';
import { useToast } from 'vue-toastification';
import { mapState } from 'vuex';

export default {
  components: {
    ProjectDetails,
    ProjectChildElementsList,
    ExperimentList,
    ExperimentFiles,
    ExperimentComments,
    ProjectMaterials,
    FileUploadSession,
    EntityOverviewLayout,
    DisplayImageGallery,
  },
  data() {
    return {
      project: null,
      workflows: [],
      childElements: [],
      rootAllowedChildTypes: [],
      structureTypesById: {},
      structureTypesByCode: {},
      experiments: [],
      files: [],
      folders: [],
      projectHierarchy: [],
      projectFilesBasePath: '',
      isFilesTreeLoading: false,
      comments: [],
      users: [],
      newUserName: '',
      materials: [],
      isUploading: false,
      uploadPercent: 0,
      uploadLabel: "",
      uploadSession: {
        visible: false,
        fileType: '',
        entityId: null,
        files: [],
        folderId: null,
      },
    };
  },
  setup() {
    const toast = useToast();
    const discussion = useEntityDiscussion();
    return { toast, discussion };
  },
  async created() {
    await this.fetchProjectData();
    await this.fetchStructureTypes();
    await this.fetchFiles();
    this.fetchWorkflows();
    this.fetchRootAllowedChildTypes();
    this.fetchChildElements();
    this.fetchExperiments();
    this.fetchComments();
    this.fetchUsers();
    this.fetchProjectMaterials()
  },
  computed: {
    ...mapState(['loggedInUser']),
    overviewLeftPanels() {
      return [
        { id: 'children', label: this.childElementsSectionTitle },
        { id: 'experiments', label: 'Experiments' },
      ];
    },
    overviewPanels() {
      return [
        { id: 'files', label: 'Files' },
        { id: 'comments', label: 'Comments' },
        { id: 'materials', label: 'Materials' },
      ];
    },
    childElementsSectionTitle() {
      const firstType = this.childElements?.[0]?.type_name;
      if (!firstType) {
        const fallbackType = this.rootAllowedChildTypes?.[0]?.name;
        return fallbackType ? this.pluralizeTypeName(fallbackType) : 'Child Elements';
      }
      return this.pluralizeTypeName(firstType);
    },
  },
  methods: {
    pluralizeTypeName(typeName) {
      const normalized = String(typeName || '').trim();
      if (!normalized) {
        return 'Child Elements';
      }

      const lower = normalized.toLowerCase();
      if (lower.endsWith('s')) {
        return normalized;
      }
      if (lower.endsWith('y') && !/[aeiou]y$/i.test(normalized)) {
        return `${normalized.slice(0, -1)}ies`;
      }
      return `${normalized}s`;
    },

    async fetchProjectData() {
      const projectId = this.$route.params.id;
      try {
        const response = await axios.get(API_ENDPOINTS.project(projectId), {
          params: { entra_id: this.loggedInUser.entra_id }
        });
        this.project = response.data;
      } catch (error) {
        this.toast.error('Failed to fetch project data');
        console.error('Failed to fetch project data:', error);
      }
    },
    async fetchProjectHierarchy() {
      if (!this.project?.id) {
        this.projectHierarchy = [];
        return;
      }

      try {
        const response = await apiService.getProjectHierarchy(
          this.project.id,
          this.loggedInUser.entra_id,
          this.project?.structure_set_id ?? null
        );
        this.projectHierarchy = Array.isArray(response?.structure) ? response.structure : [];
      } catch (error) {
        this.projectHierarchy = [];
        console.error('Failed to fetch project hierarchy:', error);
      }
    },
    async fetchWorkflows() {
      try {
        const projectId = this.$route.params.id;
        const response = await axios.get(API_ENDPOINTS.projectWorkflows(projectId), {
          params: { entra_id: this.loggedInUser.entra_id }
        });
        this.workflows = response.data;
      } catch (e) {
        if (e.response.status === 404) {
          this.workflows = []
        } else {
          this.toast.error('Failed to fetch workflows');
          console.error('Failed to fetch workflows:', e);
        }
      }
    },
    async fetchChildElements() {
      try {
        const projectId = this.$route.params.id;
        const response = await apiService.getProjectStructureElements(
          projectId,
          this.loggedInUser.entra_id,
          null,
          this.project?.structure_set_id ?? null
        );
        const items = this.normalizeCollection(response, 'elements');
        this.childElements = items.map((item) => {
          const typeFromId = item.type_id != null ? this.structureTypesById[item.type_id] : null;
          const typeFromCode = item.type_code ? this.structureTypesByCode[item.type_code] : null;
          return {
            ...item,
            type_name: item.type_name || item.type?.name || item.structure_type_name || typeFromId || typeFromCode || '-',
            parent_name: item.parent_name || item.parent?.name || 'Project',
          };
        });
      } catch (error) {
        this.childElements = [];
        this.toast.error('Failed to fetch child elements');
        console.error('Failed to fetch child elements:', error);
      }
    },
    async fetchStructureTypes() {
      try {
        const response = await apiService.getProjectStructureTypes(this.loggedInUser.entra_id);
        const items = this.normalizeCollection(response, 'types');

        this.structureTypesById = items.reduce((acc, type) => {
          acc[type.id] = type.name;
          return acc;
        }, {});

        this.structureTypesByCode = items.reduce((acc, type) => {
          if (type.code) {
            acc[type.code] = type.name;
          }
          return acc;
        }, {});

        if (this.childElements.length) {
          this.childElements = this.childElements.map((item) => {
            const typeFromId = item.type_id != null ? this.structureTypesById[item.type_id] : null;
            const typeFromCode = item.type_code ? this.structureTypesByCode[item.type_code] : null;
            return {
              ...item,
              type_name: item.type_name && item.type_name !== '-' ? item.type_name : (typeFromId || typeFromCode || '-'),
            };
          });
        }
      } catch (error) {
        this.structureTypesById = {};
        this.structureTypesByCode = {};
        console.error('Failed to fetch structure types:', error);
      }
    },
    async fetchRootAllowedChildTypes() {
      try {
        const projectId = this.$route.params.id;
        const response = await apiService.getProjectStructureAllowedChildTypes(
          projectId,
          null,
          this.loggedInUser.entra_id,
          this.project?.structure_set_id ?? null
        );
        this.rootAllowedChildTypes = this.normalizeCollection(response, 'types');
      } catch (error) {
        this.rootAllowedChildTypes = [];
        console.error('Failed to fetch allowed root child types:', error);
      }
    },
    normalizeCollection(payload, key) {
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.items)) {
        return payload.items;
      }
      if (payload && Array.isArray(payload[key])) {
        return payload[key];
      }
      if (payload && payload.data && Array.isArray(payload.data.items)) {
        return payload.data.items;
      }
      if (payload && payload.data && Array.isArray(payload.data)) {
        return payload.data;
      }
      return [];
    },
    async fetchExperiments() {
      try {
        const projectId = this.$route.params.id;
        const response = await axios.get(API_ENDPOINTS.projectExperiments(projectId), {
          params: { entra_id: this.loggedInUser.entra_id }
        });
        this.experiments = response.data;
      } catch (error) {
        if (error.response && error.response.status === 404) {
          this.experiments = [];
        } else {
          this.toast.error('Failed to fetch experiments');
          console.error('Failed to fetch experiments:', error);
        }
      }
    },
    async fetchProjectMaterials() {
      try {
        const projectId = this.$route.params.id;
        const response = await axios.get(API_ENDPOINTS.materialProject, {
          params: {
            project_id: projectId,
          }
        });
        this.materials = response.data;
      } catch (error) {
        this.toast.error('Failed to fetch projectMaterials');
      }
    },
    async fetchFiles() {
      const projectId = this.project.id;
      this.isFilesTreeLoading = true;
      try {
        const response = await axios.get(API_ENDPOINTS.filesTree, {
          params: {
            file_type: 'project',
            entity_id: projectId,
            entra_id: this.loggedInUser.entra_id
          },
        }); 
        const { files, folders, basePath } = parseFilesTree(response.data);
        this.files = files;
        this.folders = folders;
        this.projectFilesBasePath = basePath;
      } catch (error) {
        console.error('Error fetching files/folders from new endpoint:', error);
        this.files = [];
        this.folders = [];
      } finally {
        this.isFilesTreeLoading = false;
      }
    },
    normalizeFolderPath(path) {
      if (!path) return '';
      return String(path).replace(/\\/g, '/').replace(/\/+$/, '');
    },
    collectSourceFolders(folders) {
      const collected = [];
      (Array.isArray(folders) ? folders : []).forEach((folder) => {
        collected.push(folder);
        if (Array.isArray(folder?.folders) && folder.folders.length) {
          collected.push(...this.collectSourceFolders(folder.folders));
        }
      });
      return collected;
    },
    buildActualFolderTree(parentPath, sourceFolders) {
      const normalizedParentPath = this.normalizeFolderPath(parentPath);
      const children = (Array.isArray(sourceFolders) ? sourceFolders : [])
        .filter((folder) => this.normalizeFolderPath(folder?.parent_path) === normalizedParentPath)
        .filter((folder) => !folder?.entity_type);

      return children.map((folder) => ({
        ...folder,
        id: folder?.id ?? `virtual:${folder?.path || Math.random().toString(36).slice(2)}`,
        backend_id: folder?.id ?? null,
        internal_path: folder?.internal_path || folder?.path || '',
        parent_path: normalizedParentPath,
        parent_id: folder?.parent_id ?? null,
        virtual: Boolean(folder?.virtual),
        entity_type: null,
        entity_id: null,
        folders: this.buildActualFolderTree(folder?.path, sourceFolders),
        files: Array.isArray(folder?.files) ? folder.files : [],
      }));
    },
    buildHierarchyFolderTree(nodes, sourceFolders, parentPath) {
      const normalizedParentPath = this.normalizeFolderPath(parentPath);
      const currentSourceFolders = this.buildActualFolderTree(normalizedParentPath, sourceFolders);

      const folders = currentSourceFolders.map((folder) => ({
        ...folder,
        id: folder?.id ?? `virtual:${folder?.path || Math.random().toString(36).slice(2)}`,
        backend_id: folder?.id ?? null,
        internal_path: folder?.internal_path || folder?.path || '',
        parent_id: folder?.parent_id ?? null,
        virtual: Boolean(folder?.virtual),
        entity_type: folder?.entity_type || null,
        entity_id: folder?.entity_id ?? null,
      }));

      const groupedNodes = (Array.isArray(nodes) ? nodes : []).reduce((acc, node) => {
        const key = String(node?.type_name || node?.structure_type_name || node?.type_code || 'Element');
        if (!acc[key]) acc[key] = [];
        acc[key].push(node);
        return acc;
      }, {});

      Object.entries(groupedNodes).forEach(([typeName, typeNodes]) => {
        const groupPath = this.normalizeFolderPath(`${normalizedParentPath || ''}/__structure_group__/${typeName}`);
        const groupFolder = {
          id: `virtual:${groupPath}`,
          backend_id: null,
          name: this.pluralizeTypeName(typeName),
          path: groupPath,
          internal_path: groupPath,
          parent_path: normalizedParentPath,
          parent_id: null,
          virtual: true,
          entity_type: null,
          entity_id: null,
          folders: [],
          files: [],
        };

        typeNodes.forEach((node) => {
          groupFolder.folders.push(this.buildHierarchyNodeFolder(node, sourceFolders, groupPath));
        });

        folders.push(groupFolder);
      });

      return folders;
    },
    buildHierarchyNodeFolder(node, sourceFolders, parentPath) {
      const nodePath = this.normalizeFolderPath(node?.path || `${parentPath}/__structure_node__/${node?.id}`);
      const matchingEntityFolder = (Array.isArray(sourceFolders) ? sourceFolders : []).find((folder) => {
        return Number(folder?.entity_id) === Number(node?.id)
          && String(folder?.entity_type || '').toLowerCase() === 'structure_element';
      });
      const nodeFolder = {
        id: `virtual:${nodePath}`,
        backend_id: null,
        name: node?.name || 'Structure Element',
        display_entity_label: node?.type_name || node?.type_code || 'Structure Element',
        path: nodePath,
        internal_path: nodePath,
        parent_path: parentPath,
        parent_id: null,
        virtual: true,
        entity_type: 'structure_element',
        entity_id: Number(node?.id),
        folders: [],
        files: Array.isArray(matchingEntityFolder?.files) ? matchingEntityFolder.files : [],
      };

      const childNodes = Array.isArray(node?.children) ? node.children : [];
      const dynamicChildren = this.buildHierarchyFolderTree(childNodes, sourceFolders, nodePath);
      const actualChildren = this.buildActualFolderTree(nodePath, sourceFolders);
      const experimentFolders = Array.isArray(node?.experiments) && node.experiments.length
        ? [this.buildExperimentsFolder(node.experiments, sourceFolders, nodePath)]
        : [];

      nodeFolder.folders = [...actualChildren, ...dynamicChildren, ...experimentFolders];
      return nodeFolder;
    },
    buildDirectExperimentsFolder(sourceFolders, parentPath) {
      const normalizedParentPath = this.normalizeFolderPath(parentPath);
      const directExperiments = (Array.isArray(this.experiments) ? this.experiments : [])
        .filter((experiment) => experiment?.project_structure_element_id == null);
      const directExperimentIds = new Set(directExperiments.map((experiment) => Number(experiment?.id)));
      const groupPath = this.normalizeFolderPath(`${normalizedParentPath}/__experiments__`);
      const groupId = `virtual:${groupPath}`;

      const rootExperimentFolders = (Array.isArray(sourceFolders) ? sourceFolders : [])
        .filter((folder) => this.normalizeFolderPath(folder?.parent_path) === normalizedParentPath)
        .filter((folder) => String(folder?.entity_type || '').toLowerCase() === 'experiment')
        .filter((folder) => directExperimentIds.has(Number(folder?.entity_id)));

      const children = directExperiments.map((experiment) => {
        const matchingFolder = rootExperimentFolders.find((folder) => Number(folder?.entity_id) === Number(experiment?.id));
        if (matchingFolder) {
          return {
            ...matchingFolder,
            id: matchingFolder?.id ?? `virtual:${matchingFolder?.path || Math.random().toString(36).slice(2)}`,
            backend_id: matchingFolder?.id ?? null,
            internal_path: matchingFolder?.internal_path || matchingFolder?.path || '',
            parent_path: groupPath,
            parent_id: groupId,
            virtual: Boolean(matchingFolder?.virtual),
            entity_type: matchingFolder?.entity_type || null,
            entity_id: matchingFolder?.entity_id || null,
            display_entity_label: 'Experiment',
          };
        }

        const experimentPath = this.normalizeFolderPath(`${groupPath}/${experiment?.id}`);
        return {
          id: `virtual:${experimentPath}`,
          backend_id: null,
          name: experiment?.name || 'Experiment',
          display_entity_label: 'Experiment',
          path: experimentPath,
          internal_path: experimentPath,
          parent_path: groupPath,
          parent_id: groupId,
          virtual: true,
          entity_type: 'experiment',
          entity_id: Number(experiment?.id),
          folders: [],
          files: [],
        };
      });

      if (!children.length) return null;

      return {
        id: groupId,
        backend_id: null,
        name: 'Experiments',
        path: groupPath,
        internal_path: groupPath,
        parent_path: normalizedParentPath,
        parent_id: null,
        virtual: true,
        entity_type: null,
        entity_id: null,
        folders: children,
        files: [],
      };
    },
    buildExperimentsFolder(experiments, sourceFolders, parentPath) {
      const groupPath = this.normalizeFolderPath(`${parentPath}/__experiments__`);
      const groupFolder = {
        id: `virtual:${groupPath}`,
        backend_id: null,
        name: 'Experiments',
        path: groupPath,
        internal_path: groupPath,
        parent_path: parentPath,
        parent_id: null,
        virtual: true,
        entity_type: null,
        entity_id: null,
        folders: [],
        files: [],
      };

      (Array.isArray(experiments) ? experiments : []).forEach((experiment) => {
        const experimentPath = this.normalizeFolderPath(`${groupPath}/${experiment?.id}`);
        const matchingEntityFolder = (Array.isArray(sourceFolders) ? sourceFolders : []).find((folder) => {
          return Number(folder?.entity_id) === Number(experiment?.id)
            && String(folder?.entity_type || '').toLowerCase() === 'experiment';
        });
        groupFolder.folders.push({
          id: `virtual:${experimentPath}`,
          backend_id: null,
          name: experiment?.name || 'Experiment',
          path: experimentPath,
          internal_path: experimentPath,
          parent_path: groupPath,
          parent_id: null,
          virtual: true,
          entity_type: 'experiment',
          entity_id: Number(experiment?.id),
          folders: this.buildActualFolderTree(experimentPath, sourceFolders),
          files: Array.isArray(matchingEntityFolder?.files) ? matchingEntityFolder.files : [],
        });
      });

      return groupFolder;
    },
    flattenFilesFromTree(folders) {
      const files = [];
      (folders || []).forEach((folder) => {
        if (Array.isArray(folder.files) && folder.files.length > 0) {
          files.push(...folder.files);
        }
        const children = Array.isArray(folder.children)
          ? folder.children
          : Array.isArray(folder.folders)
            ? folder.folders
            : [];
        if (children.length > 0) {
          files.push(...this.flattenFilesFromTree(children));
        }
      });
      return files;
    },
    flattenFolderTree(folders, parentId = null) {
      const flattened = [];
      (folders || []).forEach(folder => {
        const stableId = folder?.id != null ? folder.id : `virtual:${folder.path}`;
        flattened.push({
          id: stableId,
          backend_id: folder?.id ?? null,
          name: folder.name,
          path: folder.path,
          internal_path: folder.path,
          parent_path: folder.parent_path,
          parent_id: parentId,
          virtual: folder.virtual || false,
          entity_type: folder.entity_type || null,
          entity_id: folder.entity_id ?? null,
          display_entity_label: folder.display_entity_label || folder.type_name || folder.type_code || folder.entity_type || null,
        });
        // Recursively flatten subfolders
        if (folder.folders && folder.folders.length > 0) {
          const subFolders = this.flattenFolderTree(folder.folders, stableId);
          flattened.push(...subFolders);
        }
      });
      return flattened;
    },
    async fetchComments() {
      try {
        this.comments = await this.discussion.fetchComments({
          entityType: 'project',
          entityId: this.$route.params.id,
          entraId: this.loggedInUser.entra_id,
        });
      } catch (error) {
        this.toast.error('Failed to fetch comments');
        console.error('Failed to fetch comments:', error);
      }
    },
    async fetchUsers() {
      try {
        this.users = await this.discussion.fetchUsers({
          entraId: this.loggedInUser.entra_id,
        });
      } catch (error) {
        this.toast.error('Failed to fetch users');
        console.error('Failed to fetch users:', error);
      }
    },
    async addFiles({ files, id, folderId, fileType }) {
      this.uploadSession = {
        visible: true,
        fileType: fileType || 'project',
        entityId: id,
        files: Array.from(files),
        folderId: folderId || null,
      };
    },

    async handleUploadComplete() {
      await this.fetchFiles();
      if (this.$refs.filesComp?.fetchFolders) {
        await this.$refs.filesComp.fetchFolders();
      }
      this.uploadSession.visible = false;
    },

    handleUploadClose() {
      this.uploadSession.visible = false;
      this.fetchFiles();
    },

    handleUploadError(error) {
      console.error('Upload session error:', error);
    },
    async removeFile(fileId) {
      try {
        await apiService.deleteFile(fileId, this.loggedInUser?.id, this.loggedInUser?.entra_id);
        this.files = this.files.filter(file => file.id !== fileId);
        this.toast.success('File removed successfully!');
      } catch (error) {
        this.toast.error('Failed to remove file');
        console.error('Failed to remove file:', error);
      }
    },
    async addComment(commentText, author_id, files) {
      try {
        await this.discussion.addComment({
          entityType: 'project',
          entityId: this.$route.params.id,
          text: commentText,
          authorId: author_id,
          files,
          entraId: this.loggedInUser.entra_id,
        });

        if (files && files.length > 0) {
          await this.fetchFiles();
        }
        await this.fetchComments();
        this.toast.success('Comment added successfully!');
      } catch (error) {
        this.toast.error('Failed to add comment');
        console.error('Failed to add comment:', error);
      }
    },
    async removeComment(commentId) {
      try {
        await this.discussion.removeComment({
          commentId,
          userId: this.loggedInUser.id,
          entraId: this.loggedInUser.entra_id,
        });
        await this.fetchComments();
        await this.fetchFiles();
        this.toast.success('Comment removed successfully!');
      } catch (error) {
        this.toast.error('Failed to remove comment');
        console.error('Failed to remove comment:', error);
      }
    },
    async updateProjectDetails(updatedProject) {
      const projectId = this.$route.params.id;
      try {
        const response = await apiService.updateResource(`${API_ENDPOINTS.projects}/${projectId}`, updatedProject, this.loggedInUser.id, this.loggedInUser.entra_id);
        this.project = response.data;
        this.fetchProjectData();
        this.toast.success('Project updated successfully!');
      } catch (error) {
        this.toast.error('Failed to update project');
        console.error('Failed to update project:', error);
      }
    },
    async handleUpdateComment(commentId, newText, Id) {
      try {
        await this.discussion.updateComment({
          commentId,
          entityType: 'project',
          entityId: Id ?? this.$route.params.id,
          text: newText,
          userId: this.loggedInUser.id,
          entraId: this.loggedInUser.entra_id,
        });
        await this.fetchComments();
        this.toast.success("Comment updated successfully!");
      } catch (error) {
        this.toast.error('Failed to update comment');
        console.error('Failed to update comment:', error);
      }
    },
  },
};
</script>

<style scoped lang="scss">
</style>
